import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import firebaseService from '../services/firebaseService';
import exportService from '../services/exportService';
import { 
  formatCurrency, 
  getQuarterAndYear, 
  getFinancialYearString,
  calculateQuarterlySummary 
} from '../services/gstCalculations';
import { 
  ChartBarIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  CurrencyRupeeIcon,
  ShoppingCartIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function Reports() {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [summary, setSummary] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Initialize with current quarter and year
  useEffect(() => {
    const now = new Date();
    const { quarter, year } = getQuarterAndYear(now);
    setSelectedQuarter(quarter);
    setSelectedYear(year.toString());
  }, []);

  // Load report data when quarter/year changes
  useEffect(() => {
    if (selectedQuarter && selectedYear) {
      loadReportData();
    }
  }, [selectedQuarter, selectedYear]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      
      const filters = {
        quarter: selectedQuarter,
        year: parseInt(selectedYear)
      };

      // Load purchases and sales for the selected quarter
      const [purchasesData, salesData] = await Promise.all([
        firebaseService.getPurchases(filters),
        firebaseService.getSales(filters)
      ]);

      setPurchases(purchasesData);
      setSales(salesData);

      // Calculate quarterly summary
      const quarterlySummary = calculateQuarterlySummary(purchasesData, salesData);
      setSummary(quarterlySummary);

    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSummary = async () => {
    if (!summary) {
      return toast.error('No data to export');
    }

    try {
      setExporting(true);
      await exportService.exportQuarterlySummary(
        summary,
        selectedQuarter,
        parseInt(selectedYear),
        userProfile?.businessName || 'Business'
      );
      toast.success('Summary exported successfully');
    } catch (error) {
      console.error('Error exporting summary:', error);
      toast.error('Failed to export summary');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPurchases = async () => {
    if (purchases.length === 0) {
      return toast.error('No purchase data to export');
    }

    try {
      setExporting(true);
      await exportService.exportPurchases(
        purchases,
        selectedQuarter,
        parseInt(selectedYear)
      );
      toast.success('Purchases exported successfully');
    } catch (error) {
      console.error('Error exporting purchases:', error);
      toast.error('Failed to export purchases');
    } finally {
      setExporting(false);
    }
  };

  const handleExportSales = async () => {
    if (sales.length === 0) {
      return toast.error('No sales data to export');
    }

    try {
      setExporting(true);
      await exportService.exportSales(
        sales,
        selectedQuarter,
        parseInt(selectedYear)
      );
      toast.success('Sales exported successfully');
    } catch (error) {
      console.error('Error exporting sales:', error);
      toast.error('Failed to export sales');
    } finally {
      setExporting(false);
    }
  };

  const handleExportComplete = async () => {
    if (!summary || (purchases.length === 0 && sales.length === 0)) {
      return toast.error('No data to export');
    }

    try {
      setExporting(true);
      await exportService.exportCompleteQuarterlyReport(
        summary,
        purchases,
        sales,
        selectedQuarter,
        parseInt(selectedYear),
        {
          businessName: userProfile?.businessName || 'Business',
          gstNumber: userProfile?.gstNumber || ''
        }
      );
      toast.success('Complete report exported successfully');
    } catch (error) {
      console.error('Error exporting complete report:', error);
      toast.error('Failed to export complete report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportGSTPortal = async () => {
    if (!summary) {
      return toast.error('No data to export');
    }

    try {
      setExporting(true);
      await exportService.exportGSTPortalFormat(
        summary,
        purchases,
        sales,
        selectedQuarter,
        parseInt(selectedYear),
        {
          businessName: userProfile?.businessName || 'Business',
          gstNumber: userProfile?.gstNumber || ''
        }
      );
      toast.success('GST portal format exported successfully');
    } catch (error) {
      console.error('Error exporting GST portal format:', error);
      toast.error('Failed to export GST portal format');
    } finally {
      setExporting(false);
    }
  };

  const StatCard = ({ title, value, subValue, icon: Icon, color = 'primary', trend }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="flex items-baseline">
                <div className={`text-2xl font-semibold text-gray-900`}>
                  {formatCurrency(value)}
                </div>
                {trend && (
                  <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                    trend > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {trend > 0 ? '+' : ''}{trend}%
                  </div>
                )}
              </dd>
              {subValue && (
                <dd className="text-sm text-gray-500 mt-1">{subValue}</dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  const years = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - 2 + i;
    return year;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            GST Reports
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Generate quarterly GST summaries and export data for filing
          </p>
        </div>
      </div>

      {/* Quarter and Year Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Select Reporting Period</h3>
        </div>
        
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              className="input"
            >
              <option value="">Select Quarter</option>
              <option value="Q1">Q1 (Apr-Jun)</option>
              <option value="Q2">Q2 (Jul-Sep)</option>
              <option value="Q3">Q3 (Oct-Dec)</option>
              <option value="Q4">Q4 (Jan-Mar)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="input"
            >
              <option value="">Select Year</option>
              {years.map(year => (
                <option key={year} value={year}>
                  {year}-{(year + 1).toString().substr(-2)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="sm:col-span-2 flex items-end">
            <button
              onClick={loadReportData}
              disabled={!selectedQuarter || !selectedYear || loading}
              className="btn-primary w-full"
            >
              {loading ? (
                <div className="spinner mr-2"></div>
              ) : (
                <ChartBarIcon className="h-4 w-4 mr-2" />
              )}
              Generate Report
            </button>
          </div>
        </div>
        
        {selectedQuarter && selectedYear && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Reporting Period:</strong> {selectedQuarter} {getFinancialYearString(new Date(parseInt(selectedYear), 3, 1))}
            </p>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Sales"
              value={summary.sales.totalAmount}
              subValue={`${summary.sales.count} transactions`}
              icon={CurrencyRupeeIcon}
              color="success"
            />
            <StatCard
              title="Total Purchases"
              value={summary.purchases.totalAmount}
              subValue={`${summary.purchases.count} invoices`}
              icon={ShoppingCartIcon}
              color="primary"
            />
            <StatCard
              title="ITC Available"
              value={summary.purchases.totalITC}
              subValue="Input Tax Credit"
              icon={DocumentTextIcon}
              color="warning"
            />
            <StatCard
              title="Net GST Payable"
              value={summary.netGSTPayable}
              subValue={summary.netGSTPayable < 0 ? 'Refund Due' : 'Amount Payable'}
              icon={ChartBarIcon}
              color={summary.netGSTPayable >= 0 ? 'error' : 'success'}
            />
          </div>

          {/* Detailed Summary */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Quarterly GST Summary
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Detailed breakdown for {selectedQuarter} {getFinancialYearString(new Date(parseInt(selectedYear), 3, 1))}
              </p>
            </div>
            
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Sales Summary */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Sales Summary</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Number of Sales:</span>
                      <span className="text-sm font-medium">{summary.sales.count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Base Amount (Excluding GST):</span>
                      <span className="text-sm font-medium">{formatCurrency(summary.sales.baseAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Output GST:</span>
                      <span className="text-sm font-medium text-red-600">{formatCurrency(summary.sales.totalOutputGST)}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-3">
                      <span className="text-sm font-medium text-gray-900">Total Sales:</span>
                      <span className="text-sm font-bold">{formatCurrency(summary.sales.totalAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Purchases Summary */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Purchases Summary</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Number of Purchases:</span>
                      <span className="text-sm font-medium">{summary.purchases.count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Base Amount (Excluding GST):</span>
                      <span className="text-sm font-medium">{formatCurrency(summary.purchases.baseAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">ITC Available:</span>
                      <span className="text-sm font-medium text-green-600">{formatCurrency(summary.purchases.totalITC)}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-3">
                      <span className="text-sm font-medium text-gray-900">Total Purchases:</span>
                      <span className="text-sm font-bold">{formatCurrency(summary.purchases.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* GST Calculation */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-md font-medium text-gray-900 mb-4">GST Calculation</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Output GST (Sales):</span>
                    <span className="text-sm font-medium">{formatCurrency(summary.sales.totalOutputGST)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Less: ITC (Purchases):</span>
                    <span className="text-sm font-medium">- {formatCurrency(summary.purchases.totalITC)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-300 pt-2">
                    <span className="text-base font-semibold text-gray-900">
                      {summary.netGSTPayable >= 0 ? 'Net GST Payable:' : 'Net GST Refundable:'}
                    </span>
                    <span className={`text-base font-bold ${
                      summary.netGSTPayable >= 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(Math.abs(summary.netGSTPayable))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Export Options */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center space-x-4 mb-4">
              <ArrowDownTrayIcon className="h-5 w-5 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900">Export Options</h3>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <button
                onClick={handleExportSummary}
                disabled={exporting}
                className="btn-primary"
              >
                {exporting ? <div className="spinner mr-2"></div> : <ArrowDownTrayIcon className="h-4 w-4 mr-2" />}
                Export Summary
              </button>
              
              <button
                onClick={handleExportPurchases}
                disabled={exporting || purchases.length === 0}
                className="btn-secondary"
              >
                {exporting ? <div className="spinner mr-2"></div> : <ShoppingCartIcon className="h-4 w-4 mr-2" />}
                Export Purchases
              </button>
              
              <button
                onClick={handleExportSales}
                disabled={exporting || sales.length === 0}
                className="btn-secondary"
              >
                {exporting ? <div className="spinner mr-2"></div> : <CurrencyRupeeIcon className="h-4 w-4 mr-2" />}
                Export Sales
              </button>
              
              <button
                onClick={handleExportComplete}
                disabled={exporting}
                className="btn-success"
              >
                {exporting ? <div className="spinner mr-2"></div> : <DocumentTextIcon className="h-4 w-4 mr-2" />}
                Complete Report
              </button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={handleExportGSTPortal}
                disabled={exporting}
                className="btn-warning"
              >
                {exporting ? <div className="spinner mr-2"></div> : <DocumentTextIcon className="h-4 w-4 mr-2" />}
                Export GSTR-3B Format
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Export in GSTR-3B format ready for GST portal submission
              </p>
            </div>
          </div>
        </>
      )}

      {/* No Data State */}
      {!loading && selectedQuarter && selectedYear && !summary && (
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No data found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No transactions found for {selectedQuarter} {getFinancialYearString(new Date(parseInt(selectedYear), 3, 1))}
          </p>
        </div>
      )}

      {/* Initial State */}
      {!selectedQuarter || !selectedYear ? (
        <div className="text-center py-12">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Select a reporting period</h3>
          <p className="mt-1 text-sm text-gray-500">
            Choose a quarter and year to generate your GST report
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default Reports;
